import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import api from '../../../services/api';
import { getUserGroupUsers, addUserGroupUser, removeUserGroupUser } from '../../../services/userGroupService';
import { parseTableParams } from '../../../utils/Utils';
import DataTable from '../../../components/ui/DataTable';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import PersonCell from '../../../components/ui/PersonCell';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import Button from '../../../components/ui/Button';
import { criterionSummary } from '../../../components/ui/criterionMeta';

export default function GroupUsersTab({ groupId, isAllUsers, groupType, criteria }) {
  // Dinamik grup BİREBİR KRİTERİN KENDİSİDİR: üye listesi salt okunurdur, elle
  // ekleme/çıkarma yoktur (backend de 409 döner). Üstte kriter özeti gösterilir ki
  // kullanıcı "bu kişiler neden burada?" sorusunun cevabını ekranda görsün.
  const isDynamic = Number(groupType) === 1;
  const readOnly = isAllUsers || isDynamic;
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, userId: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setFetching(true);
    getUserGroupUsers(groupId)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [groupId, queryParams, refreshTrigger]);

  const baseColumns = useMemo(() => [
    {
      name: 'fullName', label: t('employee.fullName'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const name = value || `${row?.firstName || ''} ${row?.lastName || ''}`.trim();
          return <PersonCell name={name} profilePicture={row?.profilePicture} />;
        },
      },
    },
    {
      name: 'email', label: t('employee.corporateEmail'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
  ], [t, data]);

  const handleRemove = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, userId: row.userId || row.id, name: row.fullName || row.email || '' });
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await removeUserGroupUser(groupId, deleteConfirm.userId);
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setDeleteConfirm({ isOpen: false, userId: null, name: '' });
    setDeleting(false);
  }, [groupId, deleteConfirm.userId]);

  const actionOptions = useMemo(() => {
    if (readOnly) return [];
    return [
      { type: 'delete', label: t('userGroup.removeUser'), onClick: handleRemove },
    ];
  }, [t, readOnly, handleRemove]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // Add users drawer
  const searchEmployees = useCallback(async (q) => {
    const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const result = res.data?.data;
    const items = Array.isArray(result) ? result : (result?.data || []);
    return items.map((e) => ({
      id: e.id,
      name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
      profilePicture: e.profilePicture,
    }));
  }, []);

  const handleAddOpen = () => {
    setSelectedUserIds([]);
    setDrawerOpen(true);
  };

  const handleAddSave = async () => {
    if (selectedUserIds.length === 0) return;
    setSaving(true);
    try {
      await addUserGroupUser(groupId, { userIds: selectedUserIds });
      setDrawerOpen(false);
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setSaving(false);
  };

  return (
    <Container>
      {isDynamic && (
        <CriteriaBanner data-testid="group-criteria-banner">
          <BannerTitle>{t('userGroup.dynamicBannerTitle')}</BannerTitle>
          <BannerText>{t('userGroup.dynamicBannerText')}</BannerText>
          {Array.isArray(criteria) && criteria.length > 0 && (
            <CriteriaChips>
              {criteria.map((k) => (
                <Chip key={k.id}>{criterionSummary(k, t)}</Chip>
              ))}
            </CriteriaChips>
          )}
        </CriteriaBanner>
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('userGroup.removeUser')}
        message={t('userGroup.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, userId: null, name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('userGroup.tabMembers')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={!readOnly}
          newButtonLabel={t('userGroup.addUser')}
          onNewButtonClick={handleAddOpen}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, readOnly,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        direction="right"
        size="45%"
        duration={150}
        overlayOpacity={0.2}
        style={{
          display: 'flex', flexDirection: 'column', maxWidth: '100vw', minWidth: 380,
          margin: '12px', borderRadius: '6px', height: 'calc(100vh - 24px)',
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)', zIndex: 1000,
        }}
      >
        <DrawerHeader>
          <DrawerTitle>{t('userGroup.addUser')}</DrawerTitle>
          <CloseBtn onClick={() => setDrawerOpen(false)}><X size={20} /></CloseBtn>
        </DrawerHeader>
        <DrawerBody>
          <AsyncGenericSelect
            label={t('employee.title')}
            value={selectedUserIds}
            onChange={setSelectedUserIds}
            onSearch={searchEmployees}
            renderOption={(o) => <PersonCell name={o.name} profilePicture={o.profilePicture} />}
            isMulti
          />
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" onClick={() => setDrawerOpen(false)} disabled={saving}>{t('common.cancel')}</Button>
          <Button onClick={handleAddSave} disabled={saving || selectedUserIds.length === 0}>
            {saving ? '...' : t('common.save')}
          </Button>
        </DrawerFooter>
      </Drawer>
    </Container>
  );
}

const Container = styled.div``;

const CriteriaBanner = styled.div`
  background: rgba(2, 211, 191, 0.07);
  border: 1px solid rgba(2, 211, 191, 0.25);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
`;
const BannerTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px; font-weight: 700; color: #004242; margin-bottom: 2px;
`;
const BannerText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px; color: #64748b; line-height: 1.45;
`;
const CriteriaChips = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;
`;
const Chip = styled.span`
  display: inline-flex; align-items: center;
  background: #fff; border: 1px solid rgba(0, 66, 66, 0.15);
  border-radius: 10px; padding: 2px 10px;
  font-family: 'Nunito Sans', sans-serif; font-size: 11px; font-weight: 600; color: #004242;
`;

const DrawerHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid #E8ECF0;
`;
const DrawerTitle = styled.h3`
  font-size: 16px; font-weight: 700; color: #004242; font-family: 'Nunito Sans', sans-serif;
`;
const CloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border: none; border-radius: 4px;
  background: none; color: #A0A8B4; cursor: pointer;
  &:hover { background: #F5F7FA; }
`;
const DrawerBody = styled.div`
  flex: 1; overflow-y: auto; padding: 24px;
`;
const DrawerFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
  padding: 16px 24px; border-top: 1px solid #E8ECF0;
`;
